import type { InternalAxiosRequestConfig, AxiosResponse } from "axios";
import {
  AGENT_ENTITY_RESOURCE_TYPES,
  isRelevantActiveResource,
  shouldIgnoreTrackerUrl,
} from "@/services/api/activeResourceRelevance";

/**
 * Structure for tracked API context
 */
export interface ApiContextData {
  endpoint: string;
  method: string;
  timestamp: number;
  responseData?: any;
  resourceId?: string;
  resourceType?: string;
  variant?: string; // e.g., "performance", "exposure", "holdings"
}

/**
 * Singleton class to track API requests and build context for ANI
 */
class ApiContextTracker {
  private static instance: ApiContextTracker;
  private contextHistory: ApiContextData[] = [];
  private readonly maxHistorySize = 20; // Keep last 20 requests
  private readonly maxAge = 5 * 60 * 1000; // Keep context for 5 minutes
  private readonly maxSameTypeAge = 30 * 1000; // Keep same-type variants for 30 seconds only
  private subscribers: Set<(context: ApiContextData[]) => void> = new Set();
  private cleanupInterval: NodeJS.Timeout | null = null;

  private constructor() {
    // Start automatic cleanup of old entries
    this.startAutoCleanup();
  }

  static getInstance(): ApiContextTracker {
    if (!ApiContextTracker.instance) {
      ApiContextTracker.instance = new ApiContextTracker();
    }
    return ApiContextTracker.instance;
  }

  /**
   * Start automatic cleanup of stale context entries
   */
  private startAutoCleanup(): void {
    // Clean up every minute
    this.cleanupInterval = setInterval(() => {
      this.cleanupOldEntries();
    }, 60 * 1000);
  }

  /**
   * Remove entries older than maxAge
   */
  private cleanupOldEntries(): void {
    const now = Date.now();
    const originalLength = this.contextHistory.length;
    
    this.contextHistory = this.contextHistory.filter(
      (ctx) => now - ctx.timestamp < this.maxAge
    );
    
    // Notify subscribers if history changed
    if (this.contextHistory.length !== originalLength) {
      this.notifySubscribers();
    }
  }

  /**
   * Stop automatic cleanup (for testing/cleanup)
   */
  stopAutoCleanup(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
  }

  /**
   * Track an API request
   */
  trackRequest(config: InternalAxiosRequestConfig): void {
    const url = config.url || "";
    const method = config.method?.toUpperCase() || "GET";

    // Extract resource information from URL patterns
    const resourceInfo = this.extractResourceInfo(url, method);

    const contextData: ApiContextData = {
      endpoint: url,
      method,
      timestamp: Date.now(),
      ...resourceInfo,
    };

    this.addToHistory(contextData);
  }

  /**
   * Track an API response with data
   */
  trackResponse(
    config: InternalAxiosRequestConfig,
    response: AxiosResponse
  ): void {
    const url = config.url || "";
    const method = config.method?.toUpperCase() || "GET";

    // Extract resource information
    const resourceInfo = this.extractResourceInfo(url, method);

    // Extract meaningful data from response
    const responseData = this.extractRelevantData(url, response.data);

    const contextData: ApiContextData = {
      endpoint: url,
      method,
      timestamp: Date.now(),
      responseData,
      ...resourceInfo,
    };

    this.addToHistory(contextData);
    this.notifySubscribers();
  }

  /**
   * Extract resource type and ID from URL patterns
   */
  /** Public for tests — same logic as axios interceptors. */
  extractResourceInfoFromUrl(
    url: string,
    _method: string = "GET",
  ): { resourceType?: string; resourceId?: string; variant?: string } {
    return this.extractResourceInfo(url, _method);
  }

  private extractResourceInfo(
    url: string,
    method: string
  ): { resourceType?: string; resourceId?: string; variant?: string } {
    if (shouldIgnoreTrackerUrl(url)) {
      return {};
    }

    const patterns = [
      // Report Builder patterns
      { regex: /\/report-builder\/([^/?]+)\/clone/, type: "report-clone" },
      { regex: /\/report-builder\/([^/?]+)/, type: "report" },
      { regex: /\/report-builder\/?$/, type: "report", id: "list" },
      
      // Report Configs patterns
      { regex: /\/report-configs\/([^/?]+)/, type: "report-config" },
      { regex: /\/report-configs\/?$/, type: "report-config", id: "list" },
      
      // Report Batches patterns
      { regex: /\/report-batches\/([^/?]+)/, type: "report-batch" },
      
      // Portfolio patterns with variants
      { regex: /\/portfolios\/([^/?]+)\/holdings/, type: "holding", useMatch: 1, variant: "holdings" },
      { regex: /\/portfolios\/([^/?]+)\/performance/, type: "performance", useMatch: 1, variant: "performance" },
      { regex: /\/portfolios\/([^/?]+)\/exposure/, type: "exposure", useMatch: 1, variant: "exposure" },
      { regex: /\/portfolios\/([^/?]+)\/transactions/, type: "transaction", useMatch: 1, variant: "transactions" },
      { regex: /\/portfolios\/([^/?]+)/, type: "portfolio" },
      { regex: /\/portfolio\/([^/?]+)/, type: "portfolio" },
      
      // Playground patterns with chart type variants
      { regex: /\/playground\/([^/?]+)\/chart\/performance/, type: "playground", useMatch: 1, variant: "performance" },
      { regex: /\/playground\/([^/?]+)\/chart\/exposure/, type: "playground", useMatch: 1, variant: "exposure" },
      { regex: /\/playground\/([^/?]+)\/chart\/correlation/, type: "playground", useMatch: 1, variant: "correlation" },
      { regex: /\/playground\/([^/?]+)/, type: "playground" },
      
      // Scenario Tracker patterns
      { regex: /\/scenario-tracker\/analyses\/([^/?]+)\/executive-summary/, type: "scenario", useMatch: 1, variant: "executive-summary" },
      { regex: /\/scenario-tracker\/analyses\/([^/?]+)\/master-analysis/, type: "scenario", useMatch: 1, variant: "master-analysis" },
      { regex: /\/scenario-tracker\/analyses\/([^/?]+)\/news-flow/, type: "scenario", useMatch: 1, variant: "news-flow" },
      { regex: /\/scenario-tracker\/analyses\/([^/?]+)/, type: "scenario" },
      { regex: /\/scenario-tracker\/analyses\/?$/, type: "scenario", id: "list" },
      
      // Worker patterns
      { regex: /\/worker-types\/([^/?]+)\/chat/, type: "worker-chat", useMatch: 1 },
      { regex: /\/worker-types\/([^/?]+)/, type: "worker-type" },
      { regex: /\/workers\/([^/?]+)\/tasks/, type: "worker-task", useMatch: 1 },
      { regex: /\/workers\/([^/?]+)/, type: "worker" },
      
      // Screener patterns
      { regex: /\/screener\/search/, type: "screener", id: "search" },
      { regex: /\/screener\/filters/, type: "screener", id: "filters" },
      { regex: /\/screener\/columns/, type: "screener", id: "columns" },
      { regex: /\/screener\/([^/?]+)/, type: "screener" },
      
      // Dashboard specific patterns (must come before generic dashboard pattern)
      { regex: /\/dashboards\/(list|insights?|hot-actions|ticker)/, type: "dashboard", id: "main" },
      { regex: /\/dashboard\/(insights?|hot-actions|ticker|metrics|pipeline)/, type: "dashboard", id: "main" },
      { regex: /\/dashboards\/([^/?]+)/, type: "dashboard" },
      { regex: /\/dashboard\/([^/?]+)/, type: "dashboard" },
      
      // Cash Flow patterns
      { regex: /\/ani-experts\/cash-flow\/chat/, type: "cash-flow", id: "chat" },
      { regex: /\/cash-flow\/([^/?]+)/, type: "cash-flow" },
      
      // Document patterns
      { regex: /\/documents\/([^/?]+)\/versions/, type: "document-version", useMatch: 1 },
      { regex: /\/documents\/([^/?]+)/, type: "document" },
      { regex: /\/cloud-documents\/([^/?]+)/, type: "document" },
      
      // Pod/Project patterns
      { regex: /\/pods\/([^/?]+)\/chat/, type: "pod", useMatch: 1 },
      { regex: /\/pods\/([^/?]+)/, type: "pod" },
      { regex: /\/projects\/([^/?]+)/, type: "project" },
      
      // Agentic API - Actions Inbox
      { regex: /\/agentic\/call-to-actions?/, type: "action", id: "inbox" },
      { regex: /\/agentic\/workers/, type: "worker", id: "agentic" },
      
      // Product patterns
      { regex: /\/products\/([^/?]+)/, type: "product" },
      
      // Holdings patterns
      { regex: /\/holdings\/([^/?]+)/, type: "holding" },
      
      // Metrics patterns
      { regex: /\/metrics/, type: "metric", id: "dashboard" },
      
      // Insights patterns
      { regex: /\/insights\/([^/?]+)/, type: "insight" },
      
      // News patterns
      { regex: /\/news/, type: "news", id: "feed" },
      
      // Watchlist patterns
      { regex: /\/watchlists\/([^/?]+)/, type: "watchlist" },
      
      // User/Company patterns
      { regex: /\/users\/([^/?]+)/, type: "user" },
      { regex: /\/company/, type: "company", id: "current" },
      
      // Generic resource patterns (e.g., /api/v1/resource/123)
      // This should be last as it's the fallback
      { regex: /\/([a-z-]+)\/([a-zA-Z0-9-]+)$/, type: "generic" },
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern.regex);
      if (match) {
        // Handle patterns with fixed IDs (like dashboard metrics)
        if ('id' in pattern && pattern.id) {
          return {
            resourceType: pattern.type,
            resourceId: pattern.id,
            variant: 'variant' in pattern ? pattern.variant : undefined,
          };
        }
        
        // Handle patterns that should use a specific match group for the ID
        // (e.g., /portfolios/{id}/holdings should use {id} not "holdings")
        if ('useMatch' in pattern && pattern.useMatch) {
          return {
            resourceType: pattern.type,
            resourceId: match[pattern.useMatch as number],
            variant: 'variant' in pattern ? pattern.variant : undefined,
          };
        }
        
        if (pattern.type === "generic") {
          const genericType = match[1];
          if (!AGENT_ENTITY_RESOURCE_TYPES.has(genericType)) {
            return {};
          }
          return {
            resourceType: genericType,
            resourceId: match[2],
            variant: undefined,
          };
        }

        return {
          resourceType: pattern.type,
          resourceId: match[1],
          variant: 'variant' in pattern ? pattern.variant : undefined,
        };
      }
    }

    return {};
  }

  /**
   * Extract relevant data from API responses based on endpoint
   */
  private extractRelevantData(url: string, data: any): any {
    if (!data) return undefined;

    // For list/array responses, get count and sample items
    if (Array.isArray(data)) {
      return {
        count: data.length,
        items: data.slice(0, 3).map((item) => ({
          id: item.id || item.name,
          name: item.name || item.title,
        })),
      };
    }

    // For object responses, extract key fields
    if (typeof data === "object") {
      const relevantFields: any = {};

      // Common fields to extract
      const fieldsToExtract = [
        "id",
        "name",
        "title",
        "type",
        "status",
        "value",
        "total",
        "count",
        "description",
      ];

      fieldsToExtract.forEach((field) => {
        if (data[field] !== undefined) {
          relevantFields[field] = data[field];
        }
      });

      return Object.keys(relevantFields).length > 0
        ? relevantFields
        : undefined;
    }

    return undefined;
  }

  /**
   * Add context data to history (with size limit, deduplication, and variant cleanup)
   */
  private addToHistory(contextData: ApiContextData): void {
    // Remove duplicate if same endpoint/method was called within last 2 seconds
    const isDuplicate = this.contextHistory.some(
      (ctx) =>
        ctx.endpoint === contextData.endpoint &&
        ctx.method === contextData.method &&
        contextData.timestamp - ctx.timestamp < 2000
    );

    if (!isDuplicate) {
      // Smart variant cleanup: If new data has a variant, remove old variants of same resource
      if (contextData.variant && contextData.resourceType && contextData.resourceId) {
        this.cleanupOldVariants(
          contextData.resourceType,
          contextData.resourceId,
          contextData.variant
        );
      }
      
      this.contextHistory.unshift(contextData);
      if (this.contextHistory.length > this.maxHistorySize) {
        this.contextHistory = this.contextHistory.slice(0, this.maxHistorySize);
      }
      
      // Clean up old entries on every add
      this.cleanupOldEntries();
    }
  }

  /**
   * Remove old variants when a new variant is loaded for the same resource
   * e.g., When "exposure" is loaded, remove old "performance" data
   */
  private cleanupOldVariants(
    resourceType: string,
    resourceId: string,
    newVariant: string
  ): void {
    const now = Date.now();
    
    this.contextHistory = this.contextHistory.filter((ctx) => {
      // Keep if it's not the same resource
      if (ctx.resourceType !== resourceType || ctx.resourceId !== resourceId) {
        return true;
      }
      
      // Keep if it has no variant (main resource)
      if (!ctx.variant) {
        return true;
      }
      
      // Keep if it's the new variant being added
      if (ctx.variant === newVariant) {
        return true;
      }
      
      // Remove if it's a different variant and older than 30 seconds
      if (now - ctx.timestamp > this.maxSameTypeAge) {
        return false;
      }
      
      return true;
    });
  }

  /**
   * Get current context history
   */
  getContextHistory(): ApiContextData[] {
    return [...this.contextHistory];
  }

  /**
   * Get context for a specific resource type
   */
  getContextByResourceType(resourceType: string): ApiContextData[] {
    return this.contextHistory.filter(
      (ctx) => ctx.resourceType === resourceType
    );
  }

  /**
   * Get most recent context for current page
   */
  getRecentContext(limit: number = 5): ApiContextData[] {
    return this.contextHistory.slice(0, limit);
  }

  /**
   * Get active resources (deduplicated, most recent first)
   */
  getActiveResources(maxAge: number = 60 * 1000): Array<{
    id: string;
    type: string;
    data?: any;
    timestamp: number;
  }> {
    const now = Date.now();
    const resources = new Map<string, {
      id: string;
      type: string;
      data?: any;
      timestamp: number;
    }>();

    // Iterate through history (most recent first)
    this.contextHistory.forEach((ctx) => {
      if (
        ctx.resourceType &&
        ctx.resourceId &&
        now - ctx.timestamp < maxAge
      ) {
        const key = `${ctx.resourceType}-${ctx.resourceId}`;
        // Only add if not already present (most recent wins)
        if (!resources.has(key)) {
          resources.set(key, {
            id: ctx.resourceId,
            type: ctx.resourceType,
            data: ctx.responseData,
            timestamp: ctx.timestamp,
          });
        }
      }
    });

    return Array.from(resources.values())
      .filter((resource) => isRelevantActiveResource(resource))
      .sort((a, b) => b.timestamp - a.timestamp); // Most recent first
  }

  /**
   * Subscribe to context updates
   */
  subscribe(callback: (context: ApiContextData[]) => void): () => void {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Notify all subscribers of context changes
   */
  private notifySubscribers(): void {
    this.subscribers.forEach((callback) => {
      callback(this.getContextHistory());
    });
  }

  /**
   * Clear context history (useful for page navigation)
   */
  clearHistory(): void {
    this.contextHistory = [];
    this.notifySubscribers();
  }

  /**
   * Generate human-readable context summary
   */
  getContextSummary(): string {
    const recent = this.getRecentContext(5);
    if (recent.length === 0) {
      return "No recent API activity";
    }

    const summaries = recent.map((ctx) => {
      const parts = [`${ctx.method} ${ctx.endpoint}`];
      
      if (ctx.resourceType && ctx.resourceId) {
        parts.push(`(${ctx.resourceType}: ${ctx.resourceId})`);
      }
      
      if (ctx.responseData) {
        if (ctx.responseData.count !== undefined) {
          parts.push(`[${ctx.responseData.count} items]`);
        } else if (ctx.responseData.name) {
          parts.push(`[${ctx.responseData.name}]`);
        }
      }
      
      return parts.join(" ");
    });

    return summaries.join("\n");
  }
}

// Export singleton instance
export const apiContextTracker = ApiContextTracker.getInstance();

export function extractApiResourceInfoFromUrl(url: string, method = "GET") {
  return apiContextTracker.extractResourceInfoFromUrl(url, method);
}

/**
 * Axios request interceptor for context tracking
 */
export const contextTrackingRequestInterceptor = (
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig => {
  apiContextTracker.trackRequest(config);
  return config;
};

/**
 * Axios response interceptor for context tracking
 */
export const contextTrackingResponseInterceptor = (
  response: AxiosResponse
): AxiosResponse => {
  if (response.config) {
    apiContextTracker.trackResponse(response.config, response);
  }
  return response;
};
